import {
    MakeLogicType,
    actions,
    afterMount,
    beforeUnmount,
    kea,
    key,
    listeners,
    path,
    props,
    reducers,
    selectors,
} from 'kea'
import { loaders } from 'kea-loaders'

import api from 'lib/api'

import { PythonKernelExecuteResponse } from '../Nodes/pythonExecution'

/** A DuckDB object a SQL node can currently SELECT from, as reported by the kernel's last run. */
export type NotebookKernelFrame = {
    name: string
    /** 'frame': a dataframe a node produced. 'table'/'view': created by SQL DDL in a DuckDB node. */
    kind: 'frame' | 'table' | 'view'
    /** [column name, DuckDB type] pairs. */
    columns: [string, string][]
    /** Null when counting would need a table scan (a DDL view). */
    row_count: number | null
    /** True when row_count is DuckDB's estimate, which doesn't track deletes and can be wildly off. */
    row_count_is_estimate?: boolean
}

export type NotebookKernelInfo = {
    backend: 'docker' | 'modal' | null
    status: string
    /** Empty unless a live kernel has run something — the backend gates this on the kernel being up. */
    frames?: NotebookKernelFrame[]
    last_used_at?: string | null
    last_error?: string | null
    runtime_id?: string | null
    kernel_id?: string | null
    kernel_pid?: number | null
    sandbox_id?: string | null
    cpu_cores?: number | null
    memory_gb?: number | null
    disk_size_gb?: number | null
    idle_timeout_seconds?: number | null
}

export type NotebookKernelInfoLogicProps = {
    shortId: string
}

export const cpuCoreOptions = [0.125, 0.25, 0.5, 1, 2, 4, 6, 8, 16, 32, 64]
export const memoryGbOptions = [0.25, 0.5, 1, 2, 4, 8, 16, 32, 64, 128, 256]
export const idleTimeoutOptions = [
    { label: '10 minutes', value: 600 },
    { label: '30 minutes', value: 1800 },
    { label: '1 hour', value: 3600 },
    { label: '3 hours', value: 10800 },
    { label: '6 hours', value: 21600 },
    { label: '12 hours', value: 43200 },
]

export type KernelActionInFlight = {
    start: boolean
    stop: boolean
    restart: boolean
    execute: boolean
    save: boolean
    refresh: boolean
}

const findClosestOptionIndex = (options: number[], value?: number | null): number => {
    if (value == null || Number.isNaN(value)) {
        return 0
    }
    const matchIndex = options.findIndex((option) => Math.abs(option - value) < 1e-6)
    if (matchIndex !== -1) {
        return matchIndex
    }
    return options.reduce((closestIndex, option, index) => {
        const closestValue = options[closestIndex]
        return Math.abs(option - value) < Math.abs(closestValue - value) ? index : closestIndex
    }, 0)
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface notebookKernelInfoLogicValues {
    actionInFlight: KernelActionInFlight
    code: string
    cpuIndex: number
    currentCpu: number
    currentIdleTimeout: number
    currentMemory: number
    executionResult: PythonKernelExecuteResponse | null
    executionResultLoading: boolean
    hasActionInFlight: boolean
    hasConfigChanges: boolean
    idleTimeoutSeconds: number
    isBusyStatus: boolean
    isEditingConfig: boolean
    isModalKernel: boolean
    isRunning: boolean
    isStarting: boolean
    kernelInfo: NotebookKernelInfo | null
    kernelInfoLoading: boolean
    localFrames: NotebookKernelFrame[]
    memoryIndex: number
    selectedCpu: number
    selectedMemory: number
    statusInfo: {
        label: string
        tone: 'danger' | 'default' | 'success' | 'warning'
    } | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface notebookKernelInfoLogicActions {
    clearExecution: () => {
        value: true
    }
    executeKernel: (code: any) => {
        code: any
    }
    executeKernelFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    executeKernelSuccess: (
        executionResult: PythonKernelExecuteResponse | null,
        payload?: {
            code: any
        }
    ) => {
        executionResult: PythonKernelExecuteResponse | null
        payload?: {
            code: any
        }
    }
    loadKernelInfo: () => any
    loadKernelInfoFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadKernelInfoSuccess: (
        kernelInfo: NotebookKernelInfo | null,
        payload?: any
    ) => {
        kernelInfo: NotebookKernelInfo | null
        payload?: any
    }
    resetConfigToKernel: () => {
        value: true
    }
    restartKernel: () => {
        value: true
    }
    saveKernelConfig: (
        config: {
            cpu_cores?: number
            idle_timeout_seconds?: number
            memory_gb?: number
        },
        nextAction: 'restart' | 'start'
    ) => {
        config: {
            cpu_cores?: number | undefined
            idle_timeout_seconds?: number | undefined
            memory_gb?: number | undefined
        }
        nextAction: 'restart' | 'start'
    }
    saveKernelConfigFailure: () => {
        value: true
    }
    setCode: (code: string) => {
        code: string
    }
    setConfigFromKernelInfo: (config: { cpuIndex: number; idleTimeoutSeconds: number; memoryIndex: number }) => {
        config: {
            cpuIndex: number
            idleTimeoutSeconds: number
            memoryIndex: number
        }
    }
    setCpuIndex: (cpuIndex: number) => {
        cpuIndex: number
    }
    setIdleTimeoutSeconds: (idleTimeoutSeconds: number) => {
        idleTimeoutSeconds: number
    }
    setMemoryIndex: (memoryIndex: number) => {
        memoryIndex: number
    }
    startKernel: () => {
        value: true
    }
    stopKernel: () => {
        value: true
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface notebookKernelInfoLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        isRunning: (kernelInfo: NotebookKernelInfo | null) => boolean
        statusInfo: (
            kernelInfo: NotebookKernelInfo | null,
            actionInFlight: KernelActionInFlight
        ) => {
            label: string
            tone: 'danger' | 'default' | 'success' | 'warning'
        } | null
        isStarting: (kernelInfo: NotebookKernelInfo | null, actionInFlight: KernelActionInFlight) => boolean
        isBusyStatus: (isStarting: boolean, actionInFlight: KernelActionInFlight) => boolean
        hasActionInFlight: (actionInFlight: KernelActionInFlight) => boolean
        isModalKernel: (kernelInfo: NotebookKernelInfo | null) => boolean
        localFrames: (kernelInfo: NotebookKernelInfo | null) => NotebookKernelFrame[]
        selectedCpu: (cpuIndex: number) => number
        selectedMemory: (memoryIndex: number) => number
        currentCpu: (kernelInfo: NotebookKernelInfo | null, selectedCpu: number) => number
        currentMemory: (kernelInfo: NotebookKernelInfo | null, selectedMemory: number) => number
        currentIdleTimeout: (kernelInfo: NotebookKernelInfo | null) => number
        hasConfigChanges: (
            kernelInfo: NotebookKernelInfo | null,
            selectedCpu: number,
            selectedMemory: number,
            idleTimeoutSeconds: number,
            currentCpu: number,
            currentMemory: number,
            currentIdleTimeout: number
        ) => boolean
    }
}

export type notebookKernelInfoLogicType = MakeLogicType<
    notebookKernelInfoLogicValues,
    notebookKernelInfoLogicActions,
    NotebookKernelInfoLogicProps,
    notebookKernelInfoLogicMeta
>

export const notebookKernelInfoLogic = kea<notebookKernelInfoLogicType>([
    props({} as NotebookKernelInfoLogicProps),
    path((key) => ['scenes', 'notebooks', 'Notebook', 'notebookKernelInfoLogic', key]),
    key(({ shortId }) => shortId),
    actions({
        executeKernel: (code) => ({ code }),
        setCode: (code: string) => ({ code }),
        setCpuIndex: (cpuIndex: number) => ({ cpuIndex }),
        setMemoryIndex: (memoryIndex: number) => ({ memoryIndex }),
        setIdleTimeoutSeconds: (idleTimeoutSeconds: number) => ({ idleTimeoutSeconds }),
        resetConfigToKernel: true,
        setConfigFromKernelInfo: (config: { cpuIndex: number; memoryIndex: number; idleTimeoutSeconds: number }) => ({
            config,
        }),
        startKernel: true,
        stopKernel: true,
        restartKernel: true,
        saveKernelConfig: (
            config: { cpu_cores?: number; memory_gb?: number; idle_timeout_seconds?: number },
            nextAction: 'start' | 'restart'
        ) => ({
            config,
            nextAction,
        }),
        saveKernelConfigFailure: true,
        clearExecution: true,
    }),
    reducers({
        code: [
            'print("Kernel ready")',
            {
                setCode: (_, { code }) => code,
            },
        ],
        cpuIndex: [
            0,
            {
                setCpuIndex: (_, { cpuIndex }) => cpuIndex,
                setConfigFromKernelInfo: (_, { config }) => config.cpuIndex,
            },
        ],
        memoryIndex: [
            0,
            {
                setMemoryIndex: (_, { memoryIndex }) => memoryIndex,
                setConfigFromKernelInfo: (_, { config }) => config.memoryIndex,
            },
        ],
        idleTimeoutSeconds: [
            idleTimeoutOptions[1].value,
            {
                setIdleTimeoutSeconds: (_, { idleTimeoutSeconds }) => idleTimeoutSeconds,
                setConfigFromKernelInfo: (_, { config }) => config.idleTimeoutSeconds,
            },
        ],
        isEditingConfig: [
            false,
            {
                setCpuIndex: () => true,
                setMemoryIndex: () => true,
                setIdleTimeoutSeconds: () => true,
                setConfigFromKernelInfo: () => false,
                resetConfigToKernel: () => false,
                saveKernelConfig: () => false,
                saveKernelConfigFailure: () => true,
            },
        ],
        actionInFlight: [
            {
                start: false,
                stop: false,
                restart: false,
                execute: false,
                save: false,
                refresh: false,
            } as KernelActionInFlight,
            {
                startKernel: (state) => ({ ...state, start: true }),
                stopKernel: (state) => ({ ...state, stop: true }),
                restartKernel: (state) => ({ ...state, restart: true }),
                executeKernel: (state) => ({ ...state, execute: true }),
                saveKernelConfig: (state) => ({ ...state, save: true }),
                loadKernelInfo: (state) => ({ ...state, refresh: true }),
                loadKernelInfoSuccess: (state, { kernelInfo }) => ({
                    ...state,
                    start: false,
                    stop: state.stop && kernelInfo?.status === 'running',
                    restart: false,
                    save: false,
                    refresh: false,
                }),
                loadKernelInfoFailure: (state) => ({
                    ...state,
                    start: false,
                    stop: false,
                    restart: false,
                    save: false,
                    refresh: false,
                }),
                executeKernelSuccess: (state) => ({ ...state, execute: false }),
                executeKernelFailure: (state) => ({ ...state, execute: false }),
                saveKernelConfigFailure: (state) => ({ ...state, save: false }),
            },
        ],
    }),
    loaders(({ props }) => ({
        kernelInfo: [
            null as NotebookKernelInfo | null,
            {
                loadKernelInfo: async () => {
                    try {
                        const response = (await api.notebooks.kernelStatus(props.shortId)) as NotebookKernelInfo
                        return response.backend ? response : null
                    } catch {
                        return null
                    }
                },
            },
        ],
        executionResult: [
            null as PythonKernelExecuteResponse | null,
            {
                executeKernel: async ({ code }): Promise<PythonKernelExecuteResponse | null> => {
                    return (await api.notebooks.kernelExecute(props.shortId, {
                        code,
                        return_variables: false,
                    })) as PythonKernelExecuteResponse
                },
            },
        ],
    })),
    selectors({
        isRunning: [
            (s) => [s.kernelInfo],
            (kernelInfo: NotebookKernelInfo | null) =>
                kernelInfo?.status === 'running' ||
                (kernelInfo?.status === 'starting' && kernelInfo?.last_error == null),
        ],
        statusInfo: [
            (s) => [s.kernelInfo, s.actionInFlight],
            (kernelInfo: NotebookKernelInfo | null, actionInFlight: KernelActionInFlight) => {
                if (!kernelInfo) {
                    return null
                }
                if (actionInFlight.stop) {
                    return { label: 'Stopping', tone: 'warning' as const }
                }
                if (actionInFlight.restart) {
                    return { label: 'Restarting', tone: 'warning' as const }
                }
                if (actionInFlight.start && kernelInfo.status !== 'starting') {
                    return { label: 'Starting', tone: 'warning' as const }
                }
                if (kernelInfo.last_error) {
                    return { label: 'Error', tone: 'danger' as const }
                }
                const statusTone: Record<
                    string,
                    { label: string; tone: 'success' | 'warning' | 'danger' | 'default' }
                > = {
                    running: { label: 'Running', tone: 'success' },
                    starting: { label: 'Starting', tone: 'warning' },
                    stopped: { label: 'Stopped', tone: 'default' },
                    timed_out: { label: 'Timed out', tone: 'warning' },
                    discarded: { label: 'Discarded', tone: 'default' },
                    error: { label: 'Error', tone: 'danger' },
                }
                return statusTone[kernelInfo.status] ?? { label: kernelInfo.status, tone: 'default' }
            },
        ],
        isStarting: [
            (s) => [s.kernelInfo, s.actionInFlight],
            (kernelInfo: NotebookKernelInfo | null, actionInFlight: KernelActionInFlight) =>
                (kernelInfo?.status === 'starting' && kernelInfo?.last_error == null) || actionInFlight.start,
        ],
        isBusyStatus: [
            (s) => [s.isStarting, s.actionInFlight],
            (isStarting: boolean, actionInFlight: KernelActionInFlight) =>
                isStarting || actionInFlight.stop || actionInFlight.restart,
        ],
        hasActionInFlight: [
            (s) => [s.actionInFlight],
            (actionInFlight: KernelActionInFlight) => Object.values(actionInFlight).some(Boolean),
        ],
        isModalKernel: [
            (s) => [s.kernelInfo],
            (kernelInfo: NotebookKernelInfo | null) => kernelInfo?.backend === 'modal',
        ],
        localFrames: [(s) => [s.kernelInfo], (kernelInfo: NotebookKernelInfo | null) => kernelInfo?.frames ?? []],
        selectedCpu: [(s) => [s.cpuIndex], (cpuIndex: number) => cpuCoreOptions[cpuIndex]],
        selectedMemory: [(s) => [s.memoryIndex], (memoryIndex: number) => memoryGbOptions[memoryIndex]],
        currentCpu: [
            (s) => [s.kernelInfo, s.selectedCpu],
            (kernelInfo: NotebookKernelInfo | null, selectedCpu: number) => kernelInfo?.cpu_cores ?? selectedCpu,
        ],
        currentMemory: [
            (s) => [s.kernelInfo, s.selectedMemory],
            (kernelInfo: NotebookKernelInfo | null, selectedMemory: number) => kernelInfo?.memory_gb ?? selectedMemory,
        ],
        currentIdleTimeout: [
            (s) => [s.kernelInfo],
            (kernelInfo: NotebookKernelInfo | null) => kernelInfo?.idle_timeout_seconds ?? idleTimeoutOptions[1].value,
        ],
        hasConfigChanges: [
            (s) => [
                s.kernelInfo,
                s.selectedCpu,
                s.selectedMemory,
                s.idleTimeoutSeconds,
                s.currentCpu,
                s.currentMemory,
                s.currentIdleTimeout,
            ],
            (
                kernelInfo: NotebookKernelInfo | null,
                selectedCpu: number,
                selectedMemory: number,
                idleTimeoutSeconds: number,
                currentCpu: number,
                currentMemory: number,
                currentIdleTimeout: number
            ) =>
                Boolean(
                    kernelInfo &&
                    (Math.abs(selectedCpu - currentCpu) > 1e-6 ||
                        Math.abs(selectedMemory - currentMemory) > 1e-6 ||
                        idleTimeoutSeconds !== currentIdleTimeout)
                ),
        ],
    }),
    listeners(({ actions, props, values }) => ({
        startKernel: async () => {
            try {
                await api.notebooks.kernelStart(props.shortId)
            } finally {
                actions.loadKernelInfo()
            }
        },
        stopKernel: async () => {
            try {
                await api.notebooks.kernelStop(props.shortId)
            } finally {
                actions.loadKernelInfo()
            }
        },
        restartKernel: async () => {
            try {
                await api.notebooks.kernelRestart(props.shortId)
            } finally {
                actions.loadKernelInfo()
            }
        },
        saveKernelConfig: async ({ config, nextAction }) => {
            try {
                await api.notebooks.kernelConfig(props.shortId, config)
            } catch {
                actions.saveKernelConfigFailure()
                return
            }
            if (nextAction === 'start') {
                actions.startKernel()
                return
            }
            actions.restartKernel()
        },
        loadKernelInfoSuccess: ({ kernelInfo }) => {
            if (!kernelInfo) {
                return
            }
            if (values.isEditingConfig && values.hasConfigChanges) {
                return
            }
            actions.setConfigFromKernelInfo({
                cpuIndex: findClosestOptionIndex(cpuCoreOptions, kernelInfo.cpu_cores),
                memoryIndex: findClosestOptionIndex(memoryGbOptions, kernelInfo.memory_gb),
                idleTimeoutSeconds: kernelInfo.idle_timeout_seconds ?? idleTimeoutOptions[1].value,
            })
        },
        resetConfigToKernel: () => {
            if (!values.kernelInfo) {
                return
            }
            actions.setConfigFromKernelInfo({
                cpuIndex: findClosestOptionIndex(cpuCoreOptions, values.kernelInfo.cpu_cores),
                memoryIndex: findClosestOptionIndex(memoryGbOptions, values.kernelInfo.memory_gb),
                idleTimeoutSeconds: values.kernelInfo.idle_timeout_seconds ?? idleTimeoutOptions[1].value,
            })
        },
        executeKernelSuccess: () => {
            actions.loadKernelInfo()
        },
        executeKernelFailure: () => {
            actions.loadKernelInfo()
        },
        clearExecution: () => {
            actions.executeKernelSuccess(null)
        },
    })),
    afterMount(({ actions, cache, values }) => {
        const scheduleRefresh = (): void => {
            const delayMs = values.isStarting ? 2000 : 10000
            cache.kernelInfoRefresh = window.setTimeout(() => {
                if (!values.actionInFlight.refresh) {
                    actions.loadKernelInfo()
                }
                scheduleRefresh()
            }, delayMs)
        }
        actions.loadKernelInfo()
        scheduleRefresh()
    }),
    beforeUnmount(({ cache }) => {
        if (cache.kernelInfoRefresh) {
            clearTimeout(cache.kernelInfoRefresh)
        }
    }),
])
